import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "row justify-content-center" }
const _hoisted_2 = { class: "col-lg-12" }
const _hoisted_3 = { class: "px-3" }
const _hoisted_4 = { class: "card bg-white border-0 rounded-3 mb-4" }
const _hoisted_5 = { class: "card-body p-4" }
const _hoisted_6 = {
  class: "nav nav-tabs justify-content-between border-0 mb-0 wizard-tabs d-none d-md-flex",
  id: "myTabstep",
  role: "tablist"
}
const _hoisted_7 = ["id", "data-bs-target", "aria-controls", "aria-selected", "onClick"]
const _hoisted_8 = { class: "fs-20 fw-bold text-primary" }
const _hoisted_9 = { class: "text-center" }
const _hoisted_10 = { class: "text-muted fw-medium" }
const _hoisted_11 = {
  class: "nav nav-tabs border-0 mb-0 wizard-tabs-mobile d-block d-md-none",
  id: "myTabstepMobile",
  role: "tablist"
}
const _hoisted_12 = ["id", "data-bs-target", "aria-controls", "aria-selected", "onClick"]
const _hoisted_13 = {
  class: "fw-bold",
  style: {"font-size":"12px"}
}
const _hoisted_14 = { class: "flex-grow-1" }
const _hoisted_15 = { class: "card bg-white border-0 rounded-3 mb-4" }
const _hoisted_16 = { class: "card-body p-4" }
const _hoisted_17 = {
  key: 0,
  class: "alert alert-warning text-warning",
  role: "alert"
}
const _hoisted_18 = ["innerHTML"]
const _hoisted_19 = {
  class: "tab-content",
  id: "myTabstep2Content"
}
const _hoisted_20 = ["id", "aria-labelledby"]
const _hoisted_21 = { class: "mb-4" }
const _hoisted_22 = { class: "text-gray-light mb-0" }

import { ref, computed, watch } from 'vue';
import FormField from '@/components/FormInputs/FormField.vue';
import schema from '../../../../../json-input/forfettario-calculator-input-schema.json';
import { evaluateCondition, calculateYearsActive, isFirstYear, isProfessionalCasse, isInpsCasse } from '@/utils/wizardHelpers';
import { t, setTranslations } from '@/utils/i18nHelper';
import { onMounted } from 'vue';

// Reactive state for the wizard

export default /*@__PURE__*/_defineComponent({
  __name: 'FormWizard',
  props: {
  initialData: {
    type: Object,
    default: () => ({}),
  },
},
  emits: ['submit-wizard'],
  setup(__props, { emit: __emit }) {

const sections = ref(schema.formSchema.sections);
const activeStepId = ref(sections.value.length > 0 ? sections.value[0].id : null);

const showAlert = ref(false);
const alertMessage = ref('');

const emit = __emit;

const props = __props;

// --- Form Data State ---
const formData = ref<Record<string, any>>({});

// Initialize formData with initialData if provided, otherwise with default values from the schema
if (Object.keys(props.initialData).length > 0) {
  formData.value = { ...props.initialData };
} else {
  schema.formSchema.sections.forEach(section => {
    section.fields.forEach(field => {
      // Set a default value based on type
      if (field.type === 'switch') {
        formData.value[field.name] = false;
      } else if (field.type === 'currency') {
        formData.value[field.name] = null;
      } else {
        // Check for a default value in validation schema
        if (field.validation && 'default' in field.validation) {
          formData.value[field.name] = field.validation.default;
        } else if (field.type === 'select') {
          formData.value[field.name] = '';
        } else {
          formData.value[field.name] = null;
        }
      }
    });
  });
}

// Helper functions object to pass to evaluateCondition
const wizardHelperFunctions = {
  calculateYearsActive,
  isFirstYear,
  isProfessionalCasse,
  isInpsCasse,
};

// Computed property to determine field visibility
const visibleFieldsForSection = computed(() => (section: any) => {
  return section.fields
    .filter((field: any) => {
      if (!field.conditionalDisplay) {
        return true; // No condition, always visible
      }

      const result = evaluateCondition(
        field.conditionalDisplay.condition,
        formData.value,
        wizardHelperFunctions
      );

      if (field.conditionalDisplay.type === 'include') {
        return result;
      } else if (field.conditionalDisplay.type === 'exclude') {
        return !result;
      }
      return true; // Default to visible if type is unknown
    })
    .map((field: any) => {
      if (field.type === 'date') {
        let maxDate = new Date(); // Default maxDate is today
        if (field.name === 'dataNascita') {
          const eighteenYearsAgo = new Date();
          eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
          maxDate = eighteenYearsAgo;
        }
        return { ...field, maxDate: maxDate };
      }
      return field;
    });
});

// --- Validation ---
const touched = ref(false);
const formValidationState = ref<Record<string, boolean>>({});
const formValidationErrors = ref<Record<string, string>>({});
const showGlobalErrors = ref(false);

// Initialize formValidationState
schema.formSchema.sections.forEach(section => {
  section.fields.forEach(field => {
    formValidationState.value[field.name] = true; // Assume valid initially
    formValidationErrors.value[field.name] = ''; // Initialize error messages
  });
});

const currentSection = computed(() => {
  return sections.value.find(s => s.id === activeStepId.value) || { fields: [] };
});

const isFieldValueValid = (field: any) => {
  const value = formData.value[field.name];
  const validation = field.validation;

  // Special validation for ENPAM birth date
  if (field.name === 'dataNascita' && formData.value.contributionRegime === 'ENPAM_MEDICI') {
    if (!value) {
      formValidationState.value[field.name] = false;
      formValidationErrors.value[field.name] = translations.value['ff.validation.dataNascita.required'] || 'Inserisci la data di nascita';
      return false;
    }
    const birthDate = new Date(value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    if (age < 18 || age > 80) {
        formValidationState.value[field.name] = false;
        formValidationErrors.value[field.name] = translations.value['ff.validation.dataNascita.age_range'] || "L'età deve essere compresa tra 18 e 80 anni";
        return false;
    }
    // If validation passes, clear any previous error
    formValidationErrors.value[field.name] = '';
  }

  // Rule 1: Required check
  if (field.required) {
    if (value === null || value === undefined || value === '') {
      formValidationState.value[field.name] = false;
      formValidationErrors.value[field.name] = field.validation?.errorMessage || 'Questo campo è obbligatorio';
      return false;
    }
  }

  // Rule 2: If not required and empty, the field is valid.
  if (!field.required && (value === null || value === undefined || value === '')) {
    formValidationState.value[field.name] = true;
    return true;
  }

  // Rule 3: If there is a value, apply detailed validation rules.
  if (validation && value !== null && value !== undefined && value !== '') {
    let isValid = true;
    const numericValue = parseFloat(value);

    switch (validation.type) {
      case 'number':
        if (isNaN(numericValue)) {
          isValid = false;
          break;
        }
        if (validation.min !== undefined && numericValue < validation.min) {
          isValid = false;
        }
        if (validation.max !== undefined && numericValue > validation.max) {
          isValid = false;
        }
        break;

      case 'string':
        if (validation.minLength !== undefined && String(value).length < validation.minLength) {
          isValid = false;
        }
        break;
    }

    formValidationState.value[field.name] = isValid;
    if (!isValid) {
      formValidationErrors.value[field.name] = validation.errorMessage || 'Valore non valido';
    } else {
      formValidationErrors.value[field.name] = '';
    }
    return isValid;
  }

  // Default to valid if no specific rules fail
  formValidationState.value[field.name] = true;
  formValidationErrors.value[field.name] = '';
  return true;
};

const validateCurrentStep = () => {
  // Only validate fields that are currently visible in the current section
  return visibleFieldsForSection.value(currentSection.value).every(field => isFieldValueValid(field));
};

const validateAllFields = () => {
  let allValid = true;
  sections.value.forEach(section => {
    // Only validate fields that are currently visible in each section
    visibleFieldsForSection.value(section).forEach(field => {
      if (!isFieldValueValid(field)) {
        allValid = false;
      }
    });
  });
  return allValid;
};

const handleCategoryChange = (selectedOption: any) => {
  if (selectedOption?.other_data?.autoSelectRegime) {
    const autoSelectedRegime = selectedOption.other_data.autoSelectRegime;
    if (COMING_SOON_REGIMES.includes(autoSelectedRegime)) {
      formData.value.contributionRegime = 'GESTIONE_SEPARATA';
    } else {
      formData.value.contributionRegime = autoSelectedRegime;
    }
  }
};

const clearHiddenFieldValues = () => {
  sections.value.forEach(section => {
    section.fields.forEach(field => {
      if (field.conditionalDisplay) {
        const shouldBeVisible = evaluateCondition(
          field.conditionalDisplay.condition,
          formData.value,
          wizardHelperFunctions
        );

        let isCurrentlyVisible = false;
        if (field.conditionalDisplay.type === 'include') {
          isCurrentlyVisible = shouldBeVisible;
        } else if (field.conditionalDisplay.type === 'exclude') {
          isCurrentlyVisible = !shouldBeVisible;
        }

        if (!isCurrentlyVisible && formData.value[field.name] !== null && formData.value[field.name] !== undefined && formData.value[field.name] !== '') {
          // Clear the value if the field is hidden and has a value
          if (field.type === 'switch') {
            formData.value[field.name] = false;
          } else if (field.type === 'currency' || field.type === 'number') {
            formData.value[field.name] = null; // Or 0, depending on desired default for numbers
          } else {
            formData.value[field.name] = ''; // Default for text, select, date, etc.
          }
        }
      }
    });
  });
};

// Watch for changes in startDate and clear dependent fields
watch(() => formData.value.startDate, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    clearHiddenFieldValues();
  }
});

const COMING_SOON_REGIMES = [
// TODO
  'GESTIONE_SEPARATA_EX_INPGI',
  'IVS_COMMERCIANTI_ENASARCO',
];

const COMING_SOON_CATEGORIES = [
  'GIORNALISTA',
  'INTERMEDIARIES',
];

watch([() => formData.value.contributionRegime, () => formData.value.category], ([newRegime, newCategory]) => {
  if (COMING_SOON_REGIMES.includes(newRegime) || COMING_SOON_CATEGORIES.includes(newCategory)) {
    alertMessage.value = '<strong>Attenzione!</strong> Il regime contributivo o la categoria selezionata è "presto disponibile". Il calcolo potrebbe non essere preciso. Verrai contattato quando sarà ultimato.';
    showAlert.value = false;
  } else {
    showAlert.value = false;
    alertMessage.value = '';
  }
});

// --- Navigation ---
const activeIndex = computed(() => sections.value.findIndex(s => s.id === activeStepId.value));
const isFirstStep = computed(() => activeIndex.value === 0);
const isLastStep = computed(() => activeIndex.value === sections.value.length - 1);

const setStep = (id: string) => {
  activeStepId.value = id;
  touched.value = false; // Reset touched state on step change
};

const nextStep = () => {
  touched.value = true;
  showGlobalErrors.value = false; // Reset global errors on next step attempt

  clearHiddenFieldValues(); // Clear values of hidden fields before validation

  if (validateCurrentStep()) {
    if (isLastStep.value) {
      if (validateAllFields()) {
                  // Temporary fix: Ensure fatturatoPrecedente is 0 if not present
          if (formData.value.fatturatoPrecedente === null || formData.value.fatturatoPrecedente === undefined || formData.value.fatturatoPrecedente === '') {
            formData.value.fatturatoPrecedente = 0;
          }
          emit('submit-wizard', formData.value);
      } else {
        showGlobalErrors.value = true;
        // Find the first invalid field and navigate to its section
        let firstInvalidSectionId: string | null = null;
        for (const section of sections.value) {
          for (const field of section.fields) {
            if (!formValidationState.value[field.name]) {
              firstInvalidSectionId = section.id;
              break;
            }
          }
          if (firstInvalidSectionId) {
            break;
          }
        }
        if (firstInvalidSectionId) {
          setStep(firstInvalidSectionId);
        }
      }
    } else {
      const nextSection = sections.value[activeIndex.value + 1];
      if (nextSection) {
        setStep(nextSection.id);
      }
    }
  }
};

const prevStep = () => {
  if (!isFirstStep.value) {
    const prevSection = sections.value[activeIndex.value - 1];
    if (prevSection) {
      setStep(prevSection.id);
    }
  }
};

// Reactive translations store
const translations = ref<Record<string, string>>({});

// CSV parser helper function
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
};

// --- Helpers ---
const getSectionTitle = (section: any) => {
  const currentYear = new Date().getFullYear();
  
  // Use reactive translations with fallback to original title
  const titleKey = `ff.sections.${section.id}.title`;
  const fallbackTitle = section.title.replace('{CURRENT_YEAR-1}', String(currentYear - 1));
  
  // Check reactive translations first, then fallback
  const translatedTitle = translations.value[titleKey] || fallbackTitle;
  
  // Replace placeholders
  return translatedTitle.replace(/\{(\w+)\}/g, (match, key) => {
    const params = { 'CURRENT_YEAR-1': currentYear - 1 };
    return params[key as keyof typeof params] !== undefined ? params[key as keyof typeof params].toString() : match;
  });
};

const getSectionDescription = (section: any) => {
  const currentYear = new Date().getFullYear();
  
  // Use reactive translations with fallback to original description  
  const descriptionKey = `ff.sections.${section.id}.description`;
  const fallbackDescription = section.description.replace('{CURRENT_YEAR-1}', String(currentYear - 1));
  
  // Check reactive translations first, then fallback
  const translatedDescription = translations.value[descriptionKey] || fallbackDescription;
  
  // Replace placeholders
  return translatedDescription.replace(/\{(\w+)\}/g, (match, key) => {
    const params = { 'CURRENT_YEAR-1': currentYear - 1 };
    return params[key as keyof typeof params] !== undefined ? params[key as keyof typeof params].toString() : match;
  });
};

// Function to parse CSV and load translations
const loadTranslationsFromCSV = async () => {
  try {
    // Fetch the CSV from public folder
    const response = await fetch('/forfettario-i18n-keys.csv');
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }
    const csvText = await response.text();
    
    // Parse CSV manually (robust parser for quoted fields)
    const lines = csvText.split('\n');
    
    // Parse first line to get headers
    const headers = parseCSVLine(lines[0]);
    const keyIndex = headers.indexOf('key');
    const textIndex = headers.indexOf('testo_attuale');
    
    if (keyIndex === -1 || textIndex === -1) {
      console.error('CSV format invalid: missing key or testo_attuale columns');
      return;
    }
    
    const parsedTranslations: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    // Process each line (skip header)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const columns = parseCSVLine(line);
      const key = columns[keyIndex]?.trim();
      let text = columns[textIndex]?.trim();

      if (key && text) {
        // Replace placeholders in the text
        text = text.replace('{CURRENT_YEAR-1}', String(currentYear - 1));
        parsedTranslations[key] = text;
      }
    }
    
    console.log('Loaded translations from CSV:', parsedTranslations);
    
    // Set reactive translations
    translations.value = parsedTranslations;
    
    // Also set in global helper for compatibility
    setTranslations(parsedTranslations);
    
  } catch (error) {
    console.error('Failed to load CSV translations:', error);
  }
};

// Load translations from CSV on mount
onMounted(() => {
  loadTranslationsFromCSV();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("ul", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: section.id,
                  class: "nav-item text-center",
                  role: "presentation"
                }, [
                  _createElementVNode("button", {
                    class: _normalizeClass(['nav-link', 'wh-48', 'bg-primary', 'bg-opacity-10', 'rounded-circle', 'p-0', 'd-block', 'mx-auto', 'mb-2', { 'active': activeStepId.value === section.id }]),
                    id: `${section.id}-step-tab`,
                    "data-bs-toggle": "tab",
                    "data-bs-target": `#${section.id}-pane`,
                    type: "button",
                    role: "tab",
                    "aria-controls": `${section.id}-pane`,
                    "aria-selected": activeStepId.value === section.id,
                    onClick: ($event: any) => (setStep(section.id))
                  }, [
                    _createElementVNode("span", _hoisted_8, _toDisplayString(index + 1), 1)
                  ], 10, _hoisted_7),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("small", _hoisted_10, _toDisplayString(getSectionTitle(section)), 1)
                  ])
                ]))
              }), 128))
            ]),
            _createElementVNode("ul", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: section.id,
                  class: "nav-item mb-1",
                  role: "presentation"
                }, [
                  _createElementVNode("button", {
                    class: _normalizeClass(['nav-link', 'd-flex', 'align-items-center', 'p-2', 'text-start', 'w-100', 'border-0', { 'active': activeStepId.value === section.id }]),
                    id: `${section.id}-step-tab-mobile`,
                    "data-bs-toggle": "tab",
                    "data-bs-target": `#${section.id}-pane`,
                    type: "button",
                    role: "tab",
                    "aria-controls": `${section.id}-pane`,
                    "aria-selected": activeStepId.value === section.id,
                    onClick: ($event: any) => (setStep(section.id))
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(['rounded-circle', 'd-flex', 'align-items-center', 'justify-content-center', 'me-2', 'flex-shrink-0', { 'bg-primary text-white': activeStepId.value === section.id, 'bg-white text-primary border border-2 border-primary': activeStepId.value !== section.id }]),
                      style: {"width":"24px","height":"24px"}
                    }, [
                      _createElementVNode("span", _hoisted_13, _toDisplayString(index + 1), 1)
                    ], 2),
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("div", {
                        class: _normalizeClass(['fw-medium', { 'text-primary': activeStepId.value === section.id, 'text-dark': activeStepId.value !== section.id }]),
                        style: {"font-size":"14px"}
                      }, _toDisplayString(getSectionTitle(section)), 3)
                    ])
                  ], 10, _hoisted_12)
                ]))
              }), 128))
            ])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          (showAlert.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                _cache[0] || (_cache[0] = _createElementVNode("i", { class: "ri-error-warning-line fs-18 me-1" }, null, -1)),
                _createElementVNode("span", { innerHTML: alertMessage.value }, null, 8, _hoisted_18)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_19, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section) => {
              return (_openBlock(), _createElementBlock("div", {
                key: section.id,
                class: _normalizeClass(["tab-pane fade", { 'show active': activeStepId.value === section.id }]),
                id: `${section.id}-pane`,
                role: "tabpanel",
                "aria-labelledby": `${section.id}-tab`,
                tabindex: "0"
              }, [
                _createElementVNode("h4", _hoisted_21, _toDisplayString(getSectionTitle(section)), 1),
                _createElementVNode("p", _hoisted_22, _toDisplayString(getSectionDescription(section)), 1),
                _cache[1] || (_cache[1] = _createElementVNode("hr", null, null, -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFieldsForSection.value(section), (field) => {
                  return (_openBlock(), _createBlock(FormField, {
                    key: field.name,
                    field: field,
                    modelValue: formData.value[field.name],
                    "onUpdate:modelValue": ($event: any) => ((formData.value[field.name]) = $event),
                    onOptionSelected: ($event: any) => (field.name === 'category' && handleCategoryChange($event)),
                    "show-error": formValidationState.value[field.name] === false && (showGlobalErrors.value || (activeStepId.value === section.id && touched.value)),
                    "error-message": formValidationErrors.value[field.name] || (field.validation && field.validation.errorMessage ? field.validation.errorMessage : 'Questo campo è obbligatorio')
                  }, null, 8, ["field", "modelValue", "onUpdate:modelValue", "onOptionSelected", "show-error", "error-message"]))
                }), 128)),
                _createElementVNode("div", {
                  class: _normalizeClass(["mt-4 d-flex", isFirstStep.value ? 'justify-content-end' : 'justify-content-between'])
                }, [
                  (!isFirstStep.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "btn btn-primary bg-primary bg-opacity-10 text-primary py-3 px-5 fw-semibold border-0",
                        onClick: prevStep
                      }, " Indietro "))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    class: "btn btn-primary py-3 px-5 fw-semibold text-white",
                    onClick: nextStep
                  }, _toDisplayString(isLastStep.value ? _unref(schema).formSchema.submitButton.text : 'Avanti'), 1)
                ], 2)
              ], 10, _hoisted_20))
            }), 128))
          ])
        ])
      ])
    ])
  ]))
}
}

})